<template>
    <div v-if="status != 'deleted'" class="col">
        <div class="card h-100 rounded-3 overflow-hidden shadow-sm border-0" :class="{'border-start border-4 border-danger': status === 'error', 'border-start border-4 border-warning': status === 'editing'}">
            <div class="card-header border-0 bg-light d-flex align-items-center gap-2">
                <i class="fa fa-file-text-o text-secondary flex-shrink-0" aria-hidden="true"></i>
                <span v-if="status !== 'editing'" class="fw-semibold text-truncate flex-grow-1 note-card-min-w-0">{{ title }}</span>
                <input v-else type="text" v-model="title" class="form-control form-control-sm flex-grow-1" />
                <span class="ms-auto text-muted small flex-shrink-0">{{ noteCreatedAt }}</span>
            </div>
            <div class="card-body">
                <div v-if="status === 'editing'">
                    <textarea v-model="content" class="form-control" rows="5"></textarea>
                </div>
                <div v-else>
                    <p class="card-text mb-0" v-html="formattedContent"></p>
                </div>
            </div>
            <div class="card-footer border-top border-light d-flex justify-content-between align-items-center bg-transparent">
                <span v-if="status === 'saved'" class="text-success small"><i class="fa fa-check me-1" aria-hidden="true"></i>Saved</span>
                <span v-else>&nbsp;</span>
                <div class="d-flex gap-1">
                    <button @click="deleteNote" class="btn btn-sm btn-outline-danger">
                        <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                    <button v-if="status !== 'editing'" @click="editNote" class="btn btn-sm btn-outline-primary">
                        <i class="fa fa-edit" aria-hidden="true"></i> Edit
                    </button>
                    <button v-if="status === 'editing'" @click="saveNote" class="btn btn-sm btn-success">
                        <i class="fa fa-save" aria-hidden="true"></i> Save
                    </button>
                    <button v-if="status === 'editing'" @click="cancelEdit" class="btn btn-sm btn-outline-secondary">
                        <i class="fa fa-times" aria-hidden="true"></i> Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
    import axios from 'axios';

    const instagramRegex = /https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/;

    function processInstagramEmbeds() {
        window.instgrm?.Embeds?.process();
    }

    export default {
        name: "NoteCard",
        props: {
            noteId: {
                type: Number,
                required: true
            },
            noteTitle: {
                type: String,
                required: true
            },
            noteContent: {
                type: String,
                required: true
            },
            noteCreatedAt: {
                type: String,
                required: true
            },
            deleteUrl: {
                type: String,
                required: true
            },
            updateUrl: {
                type: String,
                required: true
            },
        },
        data() {
            return {
                status: "init",
                title: this.noteTitle,
                content: this.noteContent,
                originalTitle: this.noteTitle,
                originalContent: this.noteContent
            };
        },
        computed: {
            formattedContent() {
                const escapeHtml = (str) => str
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/\n/g, '<br>');

                const urlRegex = /https?:\/\/[^\s]+/g;
                const instagramRegexAnchored = /^https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel)\/([A-Za-z0-9_-]+)/;
                const youtubeRegex = /^https?:\/\/(?:(?:www\.)?youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]+)/;

                const getYoutubeEmbedUrl = (url) => {
                    const m = url.match(youtubeRegex);
                    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
                };

                let result = '';
                let lastIndex = 0;
                let match;

                while ((match = urlRegex.exec(this.content)) !== null) {
                    result += escapeHtml(this.content.slice(lastIndex, match.index));

                    const url = match[0];
                    const escapedUrl = escapeHtml(url);
                    const youtubeEmbedUrl = getYoutubeEmbedUrl(url);

                    if (instagramRegexAnchored.test(url)) {
                        const permalinkUrl = url.replace(/\/?(\?.*)?$/, '/');
                        result += `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a><div class="instagram-embed-wrapper"><blockquote class="instagram-media" data-instgrm-permalink="${permalinkUrl}" data-instgrm-version="14"></blockquote></div>`;
                    } else if (youtubeEmbedUrl) {
                        result += `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a><br><iframe src="${youtubeEmbedUrl}" class="youtube-embed" frameborder="0" allowfullscreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>`;
                    } else {
                        result += `<a href="${escapedUrl}" target="_blank" rel="noopener noreferrer">${escapedUrl}</a>`;
                    }

                    lastIndex = match.index + url.length;
                }

                result += escapeHtml(this.content.slice(lastIndex));
                return result;
            },
        },
        methods: {
            deleteNote() {
                if (confirm('Are you sure you want to delete this note?')) {
                    axios.delete(this.deleteUrl)
                        .then(() => {
                            this.status = "deleted";
                        })
                        .catch(error => {
                            this.status = "error";
                            console.error('There was an error deleting the note:', error);
                        });
                }
            },
            editNote() {
                this.status = "editing";
            },
            saveNote() {
                axios.put(this.updateUrl, {
                    title: this.title,
                    content: this.content
                })
                .then(() => {
                    this.status = "saved";
                })
                .catch(error => {
                    this.status = "error";
                    console.error('There was an error updating the note:', error);
                });
            },
            cancelEdit() {
                this.title = this.originalTitle;
                this.content = this.originalContent;
                this.status = "init";
            }
        },
        watch: {
            content() {
                if (instagramRegex.test(this.content)) {
                    this.$nextTick(processInstagramEmbeds);
                }
            }
        },
        mounted() {
            if (instagramRegex.test(this.content)) {
                this.$nextTick(processInstagramEmbeds);
            }
        }
    };
</script>
  